import styled, { css, keyframes } from 'styled-components';
import InputMask from 'react-input-mask';

// Estilos do CadastroEstacaoModal seguindo o padrao "hairline shell"
// (handoff bundle Claude Design - Estacoes.html linhas 200-340).
// Usa CSS vars globais (--cco-bg etc) declaradas em global-style.js.

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 24px;
  animation: ${fadeIn} 140ms ease-out;

  @media (max-width: 720px) {
    padding: 0;
  }
`;

export const Modal = styled.div`
  width: min(880px, 100%);
  max-height: calc(100vh - 48px);
  background: var(--cco-bg, #0e0f12);
  border: 1px solid var(--cco-line-strong);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--cco-ink);
  animation: ${popIn} 180ms ease-out;

  @media (max-width: 720px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

export const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 24px var(--cco-pad) 14px;
  border-bottom: 1px solid var(--cco-line);

  @media (max-width: 480px) {
    padding: 12px var(--cco-pad);
  }
`;

export const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--cco-ink);
`;

export const Desc = styled.div`
  font-size: 13.5px;
  color: var(--cco-ink-mute);
  margin-top: 4px;
`;

export const ReqHint = styled.div`
  font-size: 11.5px;
  color: var(--cco-ink-faint);
  margin-top: 8px;
  letter-spacing: 0.04em;

  .req {
    color: var(--cco-red);
    margin-right: 3px;
  }
`;

export const Close = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  display: grid;
  place-items: center;
  color: var(--cco-ink-mute);
  cursor: pointer;
  transition: all 0.15s;
  flex: none;
  padding: 0;

  :hover {
    background: rgba(244, 68, 92, 0.1);
    color: var(--cco-red);
    border-color: rgba(244, 68, 92, 0.4);
  }

  svg {
    display: block;
  }
`;

export const ModalBody = styled.div`
  padding: 20px var(--cco-pad);
  overflow-y: auto;
  flex: 1;
`;

export const Form = styled.form`
  display: contents;
`;

// Grid de 12 colunas — replica .form-grid do design Estacoes.html.
// Em viewports estreitos cai pra 6 colunas (campos crescem proporcional)
// e finalmente 1 coluna pra mobile.
export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px 18px;

  @media (max-width: 800px) {
    grid-template-columns: repeat(6, 1fr);
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

// $cols define o span da coluna (12, 8, 6, 4). Em <800px tudo vira 6,
// em <500px tudo vira 1 (auto via parent grid).
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  grid-column: ${(props) => `span ${props.$cols || 12}`};

  @media (max-width: 800px) {
    grid-column: span 6;
  }

  @media (max-width: 720px) {
    grid-column: span 1;
  }

  @media (max-width: 500px) {
    grid-column: span 1;
  }
`;

export const Label = styled.label`
  font-size: 13.5px;
  font-weight: 600;
  color: var(--cco-ink);

  .req {
    color: var(--cco-red);
    margin-left: 2px;
  }
`;

const inputBase = css`
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: 44px;
  padding: 0 16px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  border-radius: 8px;
  color: var(--cco-ink);
  font: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  ::placeholder {
    color: var(--cco-ink-faint);
  }

  :focus {
    border-color: var(--cco-cyan);
    background: rgba(28, 176, 216, 0.06);
    box-shadow: 0 0 0 3px rgba(28, 176, 216, 0.16);
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${(props) =>
    props.$mono &&
    css`
      font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo,
        monospace;
      font-size: 13px;
      letter-spacing: 0.02em;
      font-variant-numeric: tabular-nums;
    `}

  ${(props) =>
    props.$error &&
    css`
      border-color: var(--cco-red);

      :focus {
        border-color: var(--cco-red);
        box-shadow: 0 0 0 3px rgba(244, 68, 92, 0.18);
      }
    `}
`;

export const Input = styled.input`
  ${inputBase}
`;

// Versao styled do react-input-mask — InputMask delega ref/props pra input
// nativo entao styled-components funciona normalmente.
export const MaskedInput = styled(InputMask)`
  ${inputBase}
`;

export const Select = styled.select`
  ${inputBase}
  padding-right: 42px;
  cursor: pointer;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='rgba(255,255,255,.5)' d='M0 0h12L6 8z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 16px center;

  option {
    background: #0a0a0b;
    color: var(--cco-ink);
  }
`;

export const ErrorMsg = styled.div`
  font-size: 11.5px;
  color: var(--cco-red);
  letter-spacing: 0.02em;
  margin-top: -2px;
`;

export const ModalFoot = styled.div`
  padding: 18px var(--cco-pad);
  border-top: 1px solid var(--cco-line);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 720px) {
    padding: 14px var(--cco-pad);
  }

  @media (max-width: 500px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    padding: 12px var(--cco-pad);
  }
`;

export const ConfirmHint = styled.div`
  color: var(--cco-ink-mute);
  font-size: 12.5px;
  margin-right: auto;
  letter-spacing: 0.02em;

  @media (max-width: 500px) {
    margin-right: 0;
    text-align: center;
  }
`;

export const SaveBtn = styled.button`
  height: 44px;
  padding: 0 32px;
  background: var(--cco-cyan);
  color: #06151a;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: filter 0.15s, background 0.15s, opacity 0.15s;

  :hover:not(:disabled) {
    background: #3ec0e0;
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const CancelBtn = styled.button`
  height: 44px;
  padding: 0 24px;
  background: transparent;
  color: var(--cco-ink-mute);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: 10px;
  border: 1px solid var(--cco-line);
  cursor: pointer;
  transition: all 0.15s;

  :hover {
    background: var(--cco-soft-surface);
    color: var(--cco-ink);
    border-color: var(--cco-line-strong);
  }
`;

// --- Tela de sucesso (estacaoConcluido) ----------------------------------

export const SuccessBody = styled.div`
  padding: 32px var(--cco-pad) 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  text-align: center;
  flex: 1;
  overflow-y: auto;
`;

export const SuccessIcon = styled.img`
  width: 140px;
  height: 140px;
  max-width: 40vw;
  object-fit: contain;
`;

export const SuccessMsg = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--cco-ink);
  letter-spacing: -0.005em;
  margin-top: 4px;
`;
